import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
  lockScroll,
  unlockScroll,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

const DESKTOP_MQ = window.matchMedia('(min-width: 750px)');

/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  /** @type {HTMLElement | null} */
  #hoverZoomTarget = null;

  /** @type {number | null} */
  #hoverZoomRafId = null;

  #isAnimating = false;

  get #isHalfStyle() {
    return this.dataset.zoomStyle === 'half';
  }

  /**
   * Sets up scroll listener (full style) or hover-zoom listeners (half style).
   */
  connectedCallback() {
    super.connectedCallback();
    if (!this.#isHalfStyle) {
      this.refs.dialog.addEventListener('scroll', this.handleScroll);
    } else {
      this.#hoverZoomTarget = this.querySelector('.zoom-half-panel__media');
      if (this.#hoverZoomTarget) {
        this.#hoverZoomTarget.addEventListener('mousemove', this.#handleHoverZoomMove);
        this.#hoverZoomTarget.addEventListener('mouseleave', this.#handleHoverZoomLeave);
      }
    }
  }

  /**
   * Removes scroll listener (full style) or hover-zoom listeners (half style).
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    unlockScroll(this.refs.dialog);
    if (!this.#isHalfStyle) {
      this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    }
    if (this.#hoverZoomTarget) {
      this.#hoverZoomTarget.removeEventListener('mousemove', this.#handleHoverZoomMove);
      this.#hoverZoomTarget.removeEventListener('mouseleave', this.#handleHoverZoomLeave);
      this.#hoverZoomTarget = null;
    }
    if (this.#hoverZoomRafId !== null) {
      cancelAnimationFrame(this.#hoverZoomRafId);
      this.#hoverZoomRafId = null;
    }
  }

  /**
   * Handles mousemove over the half-panel image area to apply hover zoom on desktop.
   * Throttled via requestAnimationFrame to avoid layout reads on every pointer event.
   * @param {MouseEvent} event
   */
  #handleHoverZoomMove = (event) => {
    if (!DESKTOP_MQ.matches) return;
    if (this.#hoverZoomRafId !== null) return;
    const mediaEl = /** @type {HTMLElement} */ (event.currentTarget);
    const clientX = event.clientX;
    const clientY = event.clientY;
    this.#hoverZoomRafId = requestAnimationFrame(() => {
      this.#hoverZoomRafId = null;
      const img = mediaEl.querySelector('li:not([hidden]) img.product-media__image')
               || mediaEl.querySelector('li img.product-media__image');
      const rect = (img || mediaEl).getBoundingClientRect();
      const x = ((clientX - rect.left) / rect.width) * 100;
      const y = ((clientY - rect.top) / rect.height) * 100;
      mediaEl.style.setProperty('--hover-zoom-x', `${x}%`);
      mediaEl.style.setProperty('--hover-zoom-y', `${y}%`);
      mediaEl.classList.add('is-hover-zoomed');
    });
  };

  /**
   * Handles mouseleave from the half-panel image area to reset hover zoom.
   * @param {MouseEvent} event
   */
  #handleHoverZoomLeave = (event) => {
    if (this.#hoverZoomRafId !== null) {
      cancelAnimationFrame(this.#hoverZoomRafId);
      this.#hoverZoomRafId = null;
    }
    const mediaEl = /** @type {HTMLElement} */ (event.currentTarget);
    mediaEl.style.removeProperty('--hover-zoom-x');
    mediaEl.style.removeProperty('--hover-zoom-y');
    mediaEl.classList.remove('is-hover-zoomed');
  };

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    if (this.#isHalfStyle) {
      this.#openHalf(index);
      return;
    }

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    const targetThumbnail = thumbnails.children[index];

    const open = () => {
      lockScroll(dialog);
      dialog.showModal();

      for (const target of [targetThumbnail, targetImage]) {
        target?.scrollIntoView({ behavior: 'instant' });
      }
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) return open();

    const itemTransitionName = `gallery-item-open`;
    sourceImage.style.setProperty('view-transition-name', itemTransitionName);

    const focalPoint = sourceImage.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', itemTransitionName);
    });

    document.documentElement.style.removeProperty('--gallery-media-focal-point');
    targetImage.style.removeProperty('view-transition-name');

    this.selectThumbnail(index, { behavior: 'instant' });
  }

  /**
   * Opens the half-panel dialog at a specific media index (no view transition).
   * @param {number} index - The index of the media to show.
   */
  #openHalf(index) {
    lockScroll(this.refs.dialog);
    this.refs.dialog.showModal();
    this.selectThumbnail(index, { behavior: 'instant' });
    this.loadHighResolutionImage(this.refs.media[index]);
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);
    newImage.setAttribute('ref', 'image');

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    if (this.#isHalfStyle) {
      await this.#closeHalf();
      return;
    }

    const { dialog, media } = this.refs;

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();
    const focalPoint = slide.dataset.focalPoint;
    if (focalPoint) {
      document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
    }

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', itemTransitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
    document.documentElement.style.removeProperty('--gallery-media-focal-point');
  }

  /**
   * Closes the half-panel dialog with a slide-out animation on desktop.
   * On mobile the slide-out CSS doesn't exist so we close immediately.
   * @returns {Promise<void>}
   */
  async #closeHalf() {
    const { dialog } = this.refs;
    const panel = dialog.querySelector('.zoom-half-panel');
    if (!panel || prefersReducedMotion() || !DESKTOP_MQ.matches) return this.closeDialog();

    dialog.classList.add('dialog--closing');
    await onAnimationEnd(panel);
    dialog.classList.remove('dialog--closing');
    this.closeDialog();
  }

  /**
   * Closes the dialog immediately with no animation.
   */
  closeDialog() {
    const { dialog } = this.refs;
    unlockScroll(dialog);
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (isNaN(index) || index < 0 || index >= this.refs.thumbnails.children.length) return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[index];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[index];

    if (targetImage) {
      if (this.#isHalfStyle) {
        const currentEl = media.find(el => !el.hidden);
        const shouldAnimate =
          currentEl &&
          currentEl !== targetImage &&
          options.behavior !== 'instant' &&
          !prefersReducedMotion();

        if (shouldAnimate && !this.#isAnimating) {
          this.#isAnimating = true;
          const currentIndex = media.indexOf(currentEl);
          const dir = index > currentIndex ? 1 : -1;
          this.style.setProperty('--zoom-slide-dir', String(dir));

          const isDesktop = DESKTOP_MQ.matches;
          let mediaUl = null;
          if (!isDesktop) {
            mediaUl = currentEl.closest('.zoom-half-panel__media');
            if (mediaUl) {
              const h = currentEl.getBoundingClientRect().height;
              mediaUl.style.setProperty('--zoom-anim-height', `${h}px`);
              mediaUl.classList.add('is-animating');
            }
          }

          targetImage.hidden = false;
          targetImage.classList.add('is-entering');
          currentEl.classList.add('is-leaving');

          try {
            await onAnimationEnd(targetImage);

            targetImage.classList.remove('is-entering');
            currentEl.hidden = true;
            currentEl.classList.remove('is-leaving');

            if (mediaUl) {
              mediaUl.classList.remove('is-animating');
              mediaUl.style.removeProperty('--zoom-anim-height');
            }
          } finally {
            this.#isAnimating = false;
          }
        } else {
          media.forEach((el, i) => {
            el.hidden = i !== index;
          });
        }
      } else {
        targetImage.scrollIntoView({ behavior: options.behavior });
      }

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
